import React, { type FC, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { EvTabs, EvTitle } from '@evinced-private/ui-common';

import { useDashboardFiltersStore } from 'src/common/stores/dashboardFiltersStore';

import { AccessibilitySummaryContainer } from '../../components/accessibility-summary/AccessibilitySummaryContainer';
import { BusinessUnitChartsSection } from '../../components/business-unit-charts-section/BusinessUnitChartsSection';
import { BusinessUnitLoadingStates } from '../../components/business-unit-loading-states/BusinessUnitLoadingStates';
import { DashboardFilters } from '../../components/dashboard-filters/DashboardFilters';
import { useBusinessUnitRedirect } from '../../hooks/useBusinessUnitRedirect';
import { useBusinessUnitTabs } from '../../hooks/useBusinessUnitTabs';
import { useConfigurationFilters } from '../../hooks/useConfigurationFilters';
import { useDashboardDataQuery } from '../../hooks/useDashboardDataQuery';
import { useBusinessUnitsFromConfig } from '../../stores/dashboardConfigStore';
import { DashboardRouteParams } from '../../types/DashboardTypes';

import './DashboardBusinessUnits.scss';

export const DashboardBusinessUnits: FC = () => {
	const { unitId } = useParams<DashboardRouteParams>();
	const history = useHistory();
	const { filters, setFilters } = useDashboardFiltersStore();
	const { businessUnits, isLoading: isConfigLoading } = useBusinessUnitsFromConfig();
	const { filterDefinitions, filterOptions, labelsExcludedFromDashboard } =
		useConfigurationFilters();
	const selectedPeriod = filters?.dateRange?.[0]?.value ? Number(filters.dateRange[0].value) : 30;

	const businessUnitName =
		businessUnits.find((bu) => bu.id === unitId)?.name ?? unitId ?? 'Unknown';

	const { setActiveTabIndex, tabs, tabPanels } = useBusinessUnitTabs({ businessUnitName });

	useBusinessUnitRedirect({ unitId, businessUnits, history });

	const filtersWithBusinessUnit = useMemo(() => {
		if (!unitId) {
			return filters;
		}

		return {
			...filters,
			unitId: [{ value: unitId, label: businessUnitName }]
		};
	}, [filters, unitId, businessUnitName]);

	const {
		data: dashboardDataResult,
		isLoading: isDashboardDataLoading,
		error: dashboardDataError
	} = useDashboardDataQuery(filtersWithBusinessUnit, labelsExcludedFromDashboard);

	const dashboardData = useMemo(() => {
		return dashboardDataResult?.regularPeriod ?? [];
	}, [dashboardDataResult?.regularPeriod]);

	// Handle loading and error states
	const loadingComponent = useMemo(
		() => (
			<BusinessUnitLoadingStates
				isConfigLoading={isConfigLoading}
				businessUnits={businessUnits}
				unitId={unitId}
			/>
		),
		[isConfigLoading, businessUnits, unitId]
	);

	// If we have a loading state to show, return it
	if (isConfigLoading || (!isConfigLoading && businessUnits.length === 0) || !unitId) {
		return loadingComponent;
	}

	return (
		<div className="dashboard-business-units">
			<h1 className="business-units-dashboard-title">{businessUnitName} dashboard</h1>

			<DashboardFilters
				filters={filters}
				onFilterChange={setFilters}
				filterDefinitions={filterDefinitions}
				filterOptions={filterOptions}
			/>

			{/* Accessibility Summary Section */}
			<section
				id="accessibility-summary-section"
				role="group"
				aria-labelledby="accessibility-summary-heading"
			>
				<AccessibilitySummaryContainer
					data={dashboardData}
					selectedPeriod={selectedPeriod}
					isLoading={isDashboardDataLoading}
					error={dashboardDataError}
				/>
			</section>

			<div className="business-units-content">
				<BusinessUnitChartsSection
					dashboardData={dashboardData}
					isDashboardDataLoading={isDashboardDataLoading}
					dashboardDataError={dashboardDataError}
				/>

				<section
					className="business-unit-sub-tabs-section"
					role="group"
					aria-labelledby="sub-tabs-heading"
				>
					<EvTitle
						id="sub-tabs-heading"
						titleText="Tests labeled with this business unit"
						className="sub-tabs-title"
					/>
					<div className="sub-tabs-container">
						<EvTabs
							id="business-unit-tabs"
							tabs={tabs}
							tabPanels={tabPanels}
							defaultTab={0}
							onTabSelected={setActiveTabIndex}
							ariaLabel="Business unit sub-tabs"
							dataAutomationId="business-unit-tabs"
						/>
					</div>
				</section>
			</div>
		</div>
	);
};
